/**
 * Airdrop API Service
 * Handles all API calls related to the hourly airdrop system
 */

import { getBackendUrl } from '../utils/backendUrl';

const BACKEND_URL = getBackendUrl();

export interface AirdropRound {
  hasActiveRound?: boolean;
  roundId: string;
  roundStart: string;
  roundEnd: string;
  totalPool: number;
  eligibleUsersCount: number;
  totalBetsCount: number;
  status: 'active' | 'distributing' | 'completed' | 'failed';
  timeRemainingSeconds: number;
}

export interface UserEligibility {
  hasActiveRound: boolean;
  roundId?: string;
  roundEnd?: string;
  totalPool?: number;
  eligibleUsersCount?: number;
  isEligible: boolean;
  betCount: number;
  totalVolume: number;
  hasSocialConnected: boolean;
  missingCriteria: string[];
  distributed?: boolean;
  distributionTx?: string | null;
}

export interface SocialConnectionStatus {
  hasAllConnections: boolean;
  connections: {
    platform: string;
    connected: boolean;
    username?: string;
  }[];
}

/**
 * Get current active airdrop round
 */
export async function getCurrentRound(): Promise<{
  success: boolean;
  data?: AirdropRound;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/airdrop/current`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ AIRDROP API: Error fetching current round:', error);
    return {
      success: false,
      error: 'Failed to fetch current airdrop round'
    };
  }
}

/**
 * Get user's eligibility status for current round
 */
export async function getUserEligibility(wallet: string): Promise<{
  success: boolean;
  data?: UserEligibility;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/airdrop/eligibility/${wallet}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ AIRDROP API: Error fetching eligibility:', error);
    return {
      success: false,
      error: 'Failed to fetch eligibility status'
    };
  }
}

/**
 * Get user's social connection status
 */
export async function getSocialConnectionStatus(wallet: string): Promise<{
  success: boolean;
  data?: SocialConnectionStatus;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/airdrop/social/status/${wallet}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ AIRDROP API: Error fetching social status:', error);
    return {
      success: false,
      error: 'Failed to fetch social connection status'
    };
  }
}

/**
 * Connect Twitter account using OAuth code
 */
export async function connectTwitter(
  wallet: string,
  code: string,
  codeVerifier: string
): Promise<{
  success: boolean;
  data?: { platform: string; username: string };
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/airdrop/social/connect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        walletAddress: wallet,
        platform: 'twitter',
        code,
        codeVerifier
      })
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ AIRDROP API: Error connecting Twitter:', error);
    return {
      success: false,
      error: 'Failed to connect Twitter account'
    };
  }
}

/**
 * Disconnect social account
 */
export async function disconnectSocial(
  wallet: string,
  platform: string
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/airdrop/social/disconnect`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        wallet,
        platform
      })
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ AIRDROP API: Error disconnecting social:', error);
    return {
      success: false,
      error: 'Failed to disconnect social account'
    };
  }
}

const airdropApiService = {
  getCurrentRound,
  getUserEligibility,
  getSocialConnectionStatus,
  connectTwitter,
  disconnectSocial
};

export default airdropApiService;

